import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { logError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `$${Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0})}`;

const PLAN_META = {
  sep_ira:    { color: '#6366f1', badge: 'bg-indigo-100 text-indigo-700' },
  solo_401k:  { color: '#0891b2', badge: 'bg-cyan-100 text-cyan-700' },
  simple_ira: { color: '#059669', badge: 'bg-emerald-100 text-emerald-700' },
};

export default function RetirementOptimizerPage() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [age, setAge]         = useState('');
  const [netIncome, setNet]   = useState('');

  const load = async (override = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (override.net_income || netIncome) params.set('net_income', override.net_income || netIncome);
      if (override.age || age) params.set('age', override.age || age);
      const r = await axios.get(`${API}/retirement/optimizer?${params}`, { withCredentials: true });
      setResult(r.data);
    } catch (e) { logError(e, { context: 'RetirementOptimizer.load' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 p-4">
        <div>
          <h1 className="text-3xl font-bold">{t('tools.retirement_optimizer', 'Retirement Contribution Optimizer')}</h1>
          <p className="text-muted-foreground mt-1">{t('tools.retirement_subtitle', 'Maximize your tax-advantaged savings for 2025.')}</p>
        </div>

        {/* Inputs */}
        <Card>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  {t('tools.net_income', 'Net SE income (override)')}
                </label>
                <input type="number" value={netIncome} onChange={e => setNet(e.target.value)}
                  placeholder={t('tools.auto_from_transactions', 'Auto from transactions')}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  {t('common.age', 'Your age')}
                </label>
                <input type="number" value={age} onChange={e => setAge(e.target.value)}
                  placeholder="e.g. 45"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
              </div>
              <div className="flex items-end">
                <Button onClick={() => load()} disabled={loading} className="w-full">
                  {loading ? t('common.calculating', 'Calculating…') : t('common.recalculate', 'Recalculate')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {result && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: t('tools.net_se_income', 'Net SE Income'), value: fmt(result.net_se_income) },
                { label: t('tools.marginal_rate', 'Marginal Rate'), value: `${(result.marginal_rate*100).toFixed(0)}%` },
                { label: t('tools.max_savings', 'Max Tax Savings'), value: fmt(result.max_tax_savings) },
                { label: t('tools.recommended', 'Recommended Plan'), value: result.recommended_label },
              ].map(({label, value}) => (
                <Card key={label}>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Plan cards */}
            {Object.entries(result.plans).map(([key, plan]) => {
              const isRec = key === result.recommended_plan;
              const meta  = PLAN_META[key] || { color: '#6b7280', badge: 'bg-gray-100 text-gray-700' };
              return (
                <Card key={key} className={isRec ? 'ring-2' : ''} style={{ '--tw-ring-color': meta.color }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {plan.label}
                      {isRec && <Badge className={`${meta.badge} text-xs`}>{t('tools.recommended', 'Recommended')}</Badge>}
                      {plan.catch_up_eligible && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{t('tools.catch_up', 'Catch-up eligible')}</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">{t('tools.max_contribution', 'Max Contribution')}</p>
                        <p className="text-2xl font-black text-gray-900">{fmt(plan.max_contribution)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('tools.tax_savings', 'Tax Savings')}</p>
                        <p className="text-2xl font-black text-green-600">{fmt(plan.estimated_tax_savings)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('tools.effective_cost', 'Effective Cost')}</p>
                        <p className="text-2xl font-black text-gray-700">{fmt(plan.effective_cost)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('tools.deadline', 'Deadline')}</p>
                        <p className="text-sm font-semibold text-gray-700 mt-1">{plan.deadline}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{plan.notes}</p>
                  </CardContent>
                </Card>
              );
            })}

            <p className="text-xs text-muted-foreground italic">{result.disclaimer}</p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
